import React, { useEffect, useRef, useState } from "react";
import { Dropdown, Icon, Typography, notification } from "components";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { types, constants } from "./duck";
import styles from "./Screencast.module.css";
import { VideoPreviewer } from "./components";

declare const MediaRecorder: any;

declare const navigator: any;

const Screencast: React.FC<types.ScreencastProps> = ({
  onStartScreenRecordingCallback,
  onUploadCallback,
  isRecording,
  setIsScreenRecording,
  onPreviewerCloseCallback,
}) => {
  const streamRef = useRef<any>(undefined);
  const recorderRef = useRef<any>(undefined);
  const currentSizeRef = useRef<number>(0);

  const video = useRef<Blob | null>(null);
  const [isVideoPreviewerOpened, setIsVideoPreviewerOpened] =
    useState<boolean>(false);

  const { t } = useTranslation();

  const startRecording: types.startRecording = async (
    isNeedAudioRecord = false,
  ) => {
    let message = "";
    const chunks: any[] = [];

    const initializeGetDesktopStream: types.initializeGetDesktopStream = (
      displayMediaOptions,
    ) => {
      const mediaDevices = navigator.mediaDevices as any;

      if (navigator.mediaDevices && mediaDevices.getDisplayMedia) {
        return mediaDevices.getDisplayMedia(displayMediaOptions);
      }

      return navigator.getDisplayMedia(displayMediaOptions);
    };

    const initializeStream: types.initializeStream = async (desktopStream) => {
      if (
        isNeedAudioRecord &&
        navigator.mediaDevices &&
        navigator.mediaDevices.getUserMedia
      ) {
        try {
          const voiceStream = await navigator.mediaDevices.getUserMedia({
            video: false,
            audio: true,
          });
          streamRef.current = new MediaStream(
            desktopStream.getTracks().concat(voiceStream.getAudioTracks()),
          );
        } catch {
          streamRef.current = desktopStream;
        }
      } else {
        streamRef.current = desktopStream;
      }
    };

    const handleStopRecording = (): void => {
      video.current = new Blob(chunks, { type: "video/webm" });
      setIsVideoPreviewerOpened(true);
    };

    try {
      if (MediaRecorder) {
        const getDesktopStream = await initializeGetDesktopStream({
          video: true,
          audio: isNeedAudioRecord,
        });

        let desktopStream: any;
        try {
          desktopStream = await getDesktopStream;
        } catch {
          message = "was canceled";
        }

        await initializeStream(desktopStream);

        recorderRef.current = new MediaRecorder(streamRef.current, {
          mimeType: "video/webm",
        });

        recorderRef.current.ondataavailable = (e: any) => {
          if (e.data.size > 0) {
            if (
              currentSizeRef.current + e.data.size <=
              constants.MAX_SIZE_BYTES_FOR_SCREENCAST
            ) {
              chunks.push(e.data);
              currentSizeRef.current += e.data.size;
            } else {
              notification.createInfo({
                title: t("feedback.maximumFileSizeReachedTitle"),
                content: t("feedback.maximumFileSizeReachedDescription"),
              });
              setIsScreenRecording(false);
              currentSizeRef.current = 0;
            }
          }
        };
        recorderRef.current.onstop = handleStopRecording;
        recorderRef.current.start(1000);
        onStartScreenRecordingCallback();
      }
    } catch {
      if (!message) {
        notification.createError({
          title: t("feedback.cannotRecord"),
          content: t("feedback.screencastIsNotSupported"),
        });
      }
    }
  };

  const startRecordingWithSound = (): void => {
    startRecording(true).catch(() => {});
  };

  const startRecordingWithoutSound = (): void => {
    startRecording().catch(() => {});
  };

  const stopRecording = (): void => {
    if (recorderRef.current && streamRef.current) {
      recorderRef.current.stop();
      streamRef.current.getTracks().forEach((track: any) => track.stop());
      recorderRef.current = undefined;
      streamRef.current = undefined;
      video.current = null;
    }
  };

  useEffect(() => {
    if (!isRecording) {
      stopRecording();
    }
  }, [isRecording]);

  return (
    <>
      <Dropdown
        label={t("feedback.screenCapture")}
        prefix={<Icon name="video-camera-outlined" />}
        className={styles.screenCapture}
        buttonClassName={(openedCapture) =>
          classNames(styles.screenCaptureOpener, {
            [styles.opened]: openedCapture,
          })
        }
        contentContainerClassName={styles.screenCaptureDropdown}
        contentClassName={styles.screenCaptureContent}
      >
        <Typography variant="s1" className={styles.screenCaptureTitle}>
          {t("feedback.startScreenCapture")}
        </Typography>
        <ul className={styles.screenCaptureList}>
          <li>
            <button
              type="button"
              className={styles.screenCaptureButton}
              onClick={startRecordingWithSound}
            >
              {t("feedback.withSoundRecording")}
              <Icon name="micro-filled" className={styles.icon} />
            </button>
          </li>
          <li>
            <button
              type="button"
              className={styles.screenCaptureButton}
              onClick={startRecordingWithoutSound}
            >
              {t("feedback.withoutSound")}
            </button>
          </li>
        </ul>
      </Dropdown>
      <VideoPreviewer
        opened={isVideoPreviewerOpened}
        onCloseCallback={() => {
          setIsVideoPreviewerOpened(false);
          onPreviewerCloseCallback();
        }}
        onUploadCallback={(file) => {
          setIsVideoPreviewerOpened(false);
          onUploadCallback(file);
        }}
        video={video.current}
      />
    </>
  );
};

export default Screencast;
